import React, { useState, useEffect, useRef } from 'react';
import flatpickr from 'flatpickr';

const DateTimeSelect = ({ voucherData, experienceId, sessionToken, customerEmail, onBack, onSuccess }) => {
    const [availability, setAvailability] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedTime, setSelectedTime] = useState('');
    const dateInputRef = useRef(null);
    const fpInstanceRef = useRef(null);

    useEffect(() => {
        const fetchAvailability = async () => {
            try {
                const url = new URL('/wp-json/rll/v1/booking/availability', window.location.origin);
                let targetId = experienceId || voucherData?.product_id;

                // Dev mode fallback when jumping straight to this step
                if (!targetId && process.env.NODE_ENV === 'development') {
                    targetId = 'dev_mock';
                }

                url.searchParams.append('experience_id', targetId || '');
                const response = await fetch(url, {
                    headers: { 'Authorization': `Bearer ${sessionToken}` },
                });
                const data = await response.json();
                if (!response.ok) throw new Error(data.message || 'Failed to fetch availability');
                setAvailability(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchAvailability();
    }, [experienceId, voucherData, sessionToken]);

    // Initialize Flatpickr once availability is loaded
    useEffect(() => {
        if (!availability || !dateInputRef.current) return;

        fpInstanceRef.current = flatpickr(dateInputRef.current, {
            enable: availability.available_dates,
            dateFormat: 'Y-m-d',
            altInput: true,
            altFormat: 'F j, Y',
            minDate: 'today',
            maxDate: voucherData?.expires_at || null,
            disableMobile: true,
            onChange: (selectedDates, dateStr) => {
                setSelectedDate(dateStr);
                setSelectedTime('');
            },
        });

        return () => {
            if (fpInstanceRef.current) fpInstanceRef.current.destroy();
        };
    }, [availability]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError('');

        try {
            const response = await fetch('/wp-json/rll/v1/booking/confirm', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    session_token:  sessionToken  || 'dev_mock_token',
                    experience_id:  String(experienceId || voucherData?.product_id || ''),
                    booking_date:   selectedDate,
                    booking_time:   selectedTime || '',
                    customer_email: customerEmail || '',
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Booking failed. Please try again.');
            }

            onSuccess(data.booking_reference);
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="rll-spinner-wrap">
                <div className="rll-spinner" />
                <p>Checking available dates...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                <div className="rll-alert rll-alert--error">{error}</div>
                <button onClick={onBack} className="rll-btn rll-btn--link">Go Back</button>
            </div>
        );
    }

    const availableSlots = selectedDate && availability?.slots ? availability.slots[selectedDate] : [];

    return (
        <div className="rll-animate-in">
            {/* Experience Preview Card */}
            {availability?.product && (
                <div className="rll-experience-card" style={{ marginBottom: '2rem', cursor: 'default', maxWidth: 400 }}>
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <img
                            src={availability.product.image}
                            alt={availability.product.title}
                            style={{ width: '120px', height: '120px', objectFit: 'cover' }}
                        />
                        <div className="rll-experience-card-body" style={{ flex: 1, padding: '1rem 1.25rem' }}>
                            <h4 style={{ margin: '0 0 0.25rem', fontSize: '1.2rem' }}>{availability.product.title}</h4>
                            <p style={{ margin: '0 0 0.5rem', fontSize: '0.9rem', color: 'var(--rll-gray-500)' }}>
                                {availability.product.description}
                            </p>
                            <a
                                href={availability.product.permalink}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ display: 'inline-flex', alignItems: 'center', fontSize: '0.9rem', color: 'var(--rll-red)', fontWeight: 600, textDecoration: 'none' }}
                            >
                                View Experience
                                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ marginLeft: 4 }}>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
            )}
            <div className="rll-section-header">
                <div>
                    <h3>Choose Date & Time</h3>
                    <p>Select when you'd like to book your experience.</p>
                </div>
                <button onClick={onBack} className="rll-back-btn" aria-label="Go back">
                    <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                </button>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Flatpickr Date Input */}
                <div className="rll-datepicker-wrap">
                    <label htmlFor="rll_booking_date">Select Date</label>
                    <input
                        type="text"
                        id="rll_booking_date"
                        ref={dateInputRef}
                        placeholder="Pick an available date..."
                        readOnly
                        hidden
                    />
                </div>

                {/* Time Slots */}
                {selectedDate && availability.type === 'time_slotted' && (
                    <div className="rll-timeslot-section rll-animate-in">
                        <h4>Available Times</h4>
                        {availableSlots && availableSlots.length > 0 ? (
                            <div className="rll-timeslot-grid">
                                {availableSlots.map((slot) => (
                                    <button
                                        key={slot.time}
                                        type="button"
                                        disabled={!slot.available}
                                        onClick={() => setSelectedTime(slot.time)}
                                        className={`rll-timeslot ${!slot.available ? 'disabled' : ''} ${selectedTime === slot.time ? 'selected' : ''}`}
                                    >
                                        {slot.time}
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <p style={{ fontSize: '0.85rem', color: '#868e96', fontStyle: 'italic' }}>
                                No specific time slots available for this date.
                            </p>
                        )}
                    </div>
                )}

                <div style={{ marginTop: '2rem' }}>
                    <button
                        type="submit"
                        disabled={submitting || !selectedDate || (availability.type === 'time_slotted' && !selectedTime)}
                        className="rll-btn rll-btn--primary"
                    >
                        {submitting ? (
                            <>
                                <span className="rll-spinner" style={{ width: 18, height: 18, borderWidth: 2, marginBottom: 0 }} />
                                Confirming Booking...
                            </>
                        ) : (
                            'Confirm Booking'
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default DateTimeSelect;
